import axios from 'axios';
import logger from '../utils/logger';

// In production, use relative URL (same domain as checkout)
// In development, use explicit localhost URL
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000/api');

// Error handler
const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error
    throw new Error(error.response.data.error || 'Server error occurred');
  } else if (error.request) {
    // Network error
    throw new Error('Network error. Please check your connection.');
  } else {
    // Other errors
    throw new Error('An unexpected error occurred');
  }
};

export const api = {
  // Check if cart should redirect (lightweight, fast check)
  checkCartRedirect: async (cartId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/checkout/redirect/${cartId}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Get cart details
  getCart: async (cartId, filters = {}) => {
    try {
      // Build query params for geographic filtering
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.state) params.append('state', filters.state);
      if (filters.zip) params.append('zip', filters.zip);

      const queryString = params.toString();
      const url = queryString
        ? `${API_BASE_URL}/cart/${cartId}/?${queryString}`
        : `${API_BASE_URL}/cart/${cartId}/`;

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Generate Braintree client token
  getBraintreeToken: async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/braintree/token/`);
      return response.data.client_token;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Process checkout
  processCheckout: async (data) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/checkout/process/`, data);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Get order details
  getOrder: async (orderId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/order/${orderId}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Get checkout configuration
  getCheckoutConfig: async (domain) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/config/${domain}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Update cart email
  updateCartEmail: async (cartId, email) => {
    try {
      const response = await axios.patch(`${API_BASE_URL}/cart/${cartId}/email/`, { email });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Get shipping methods from Shopify Storefront API (filtered by address)
  getShippingMethods: async (cartId, shippingAddress) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/cart/shipping-methods/`, {
        cart_id: cartId,
        shipping_address: {
          address1: shippingAddress.address1 || '',
          address2: shippingAddress.address2 || '',
          city: shippingAddress.city || '',
          provinceCode: shippingAddress.provinceCode || shippingAddress.state || '',
          zip: shippingAddress.zip || '',
          countryCode: shippingAddress.countryCode || 'US',
          firstName: shippingAddress.firstName || '',
          lastName: shippingAddress.lastName || ''
        }
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  },

  // Sync customer info with Shopify for abandoned cart tracking
  syncCustomer: async (cartId, email, phone, shippingAddress = null, options = {}) => {
    try {
      const payload = {
        cart_id: cartId
      };

      // Add email if provided
      if (email) {
        payload.customer_email = email;
      }

      // Add phone if provided
      if (phone) {
        payload.customer_phone = phone;
      }
      logger.debug('📱 [PHONE] syncCustomer payload:', { phone, payload_phone: payload.customer_phone });

      // Add shipping address if provided
      if (shippingAddress) {
        payload.shipping_address = shippingAddress;
      }

      // Add optional fields (country, marketing preferences, billing address, mobile phone)
      if (options.country) {
        payload.country = options.country;
      }
      if (options.accepts_email_marketing !== undefined) {
        payload.accepts_email_marketing = options.accepts_email_marketing;
      }
      if (options.accepts_sms_marketing !== undefined) {
        payload.accepts_sms_marketing = options.accepts_sms_marketing;
      }
      if (options.billing_address) {
        payload.billing_address = options.billing_address;
      }
      // Mobile phone for SMS marketing (not sent to Shopify)
      if (options.mobile_phone) {
        payload.customer_mobile_phone = options.mobile_phone;
      }

      const response = await axios.post(`${API_BASE_URL}/cart/sync-customer/`, payload);
      return response.data;
    } catch (error) {
      // Fail silently for abandoned cart tracking
      logger.warn('Failed to sync customer with Shopify:', error.message);
      return null;
    }
  },

  // Get provinces/states for a specific country
  getProvinces: async (domain, countryCode) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/config/${domain}/provinces/${countryCode}/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }
};
